import { Fragment } from "react"
import { Extra, ModalidadGrupoTarifa, ReservaTarifa, TiposAlojamientos, TiposExtras } from "src/gql/schema"
import Detail from "src/shared/components/data-display/Detail/Detail"
import DetailContent from "src/shared/components/data-display/Detail/kit/DetailContent/DetailContent"
import DetailIcon from "src/shared/components/data-display/Detail/kit/DetailIcon/DetailIcon"
import DetailRow from "src/shared/components/data-display/Detail/kit/DetailRow/DetailRow"
import DetailText from "src/shared/components/data-display/Detail/kit/DetailText/DetailText"
import ResumenSection from "src/shared/components/data-display/Resumen/ResumenSection/ResumenSection"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import { formatCurrencyToFixed } from "src/shared/hooks/formatCurrency"
import { sum } from "src/shared/helpers/calculator"
import { groupExtrasTarifasByTarifa, ExtraTarifaAgrupada } from "src/shared/helpers/groupExtrasTarifasByTarifa"
import { useDate } from "src/shared/hooks/useDate"
import { capitalize } from "src/shared/helpers/capitalize"
import EntradaHospedajeMotel from "./sections/EntradaHospedajeMotel"

// Evitar repeticion de de costo unitario con el mismo precio para entregar uno solo con la sumatoria de totales
const mergeEntradasByPrecio = (entradas: ExtraTarifaAgrupada['entradas']): ExtraTarifaAgrupada['entradas'] => {
    const map = new Map<number, { fecha_inicio: string; fecha_fin: string; total: number; numero: number }>()
    for (const entry of entradas) {
        const precio = entry.subtotales?.precio_unitario || 0
        const existing = map.get(precio)
        if (existing) {
            existing.total += entry.subtotales?.total || 0
            if (entry.fecha_inicio < existing.fecha_inicio) existing.fecha_inicio = entry.fecha_inicio
            if (entry.fecha_fin > existing.fecha_fin) existing.fecha_fin = entry.fecha_fin
        } else {
            map.set(precio, {
                fecha_inicio: entry.fecha_inicio,
                fecha_fin: entry.fecha_fin,
                total: entry.subtotales?.total || 0,
                numero: entry.subtotales?.numero || 0,
            })
        }
    }
    return Array.from(map.entries()).map(([precio_unitario, e]) => ({
        fecha_inicio: e.fecha_inicio,
        fecha_fin: e.fecha_fin,
        subtotales: { precio_unitario, total: e.total, numero: e.numero },
    }))
}

const buildGruposFromReservaTarifas = (extra: Extra, reservas_tarifas: ReservaTarifa[]): ExtraTarifaAgrupada[] => {
    // Level 1: group by grupo_tarifa_id
    const grupoMap = new Map<string, { rt: ReservaTarifa; items: ReservaTarifa[] }>()

    for (const rt of reservas_tarifas) {
        const key = rt.tarifa?.grupo_tarifa_id || rt.tarifa_id
        if (!key || !rt.tarifa) continue
        const existing = grupoMap.get(key)
        if (existing) {
            existing.items.push(rt)
        } else {
            grupoMap.set(key, { rt, items: [rt] })
        }
    }

    return Array.from(grupoMap.values()).map(({ rt, items }) => {
        // Level 2: within group, merge entries with same costoUnitario
        const precioMap = new Map<number, { fecha_inicio: string; fecha_fin: string; total: number }>()

        for (const item of items) {
            const costoUnitario =
                extra.tipo_extra === TiposExtras.Persona
                    ? item.tarifa?.costo_persona_extra || 0
                    : extra.tipo_extra === TiposExtras.Hora
                        ? item.tarifa?.costo_hora_extra || 0
                        : item.tarifa?.costo_hospedaje_extra || 0

            const noches = item.subtotales?.noches || 1
            const entryTotal = costoUnitario * (extra.numero || 1) * (extra.tipo_extra !== TiposExtras.Hora ? noches : 1)

            const existing = precioMap.get(costoUnitario)
            if (existing) {
                existing.total += entryTotal
                if (item.fecha_inicio < existing.fecha_inicio) existing.fecha_inicio = item.fecha_inicio
                if (item.fecha_fin > existing.fecha_fin) existing.fecha_fin = item.fecha_fin
            } else {
                precioMap.set(costoUnitario, { fecha_inicio: item.fecha_inicio, fecha_fin: item.fecha_fin, total: entryTotal })
            }
        }

        const entradas = Array.from(precioMap.entries()).map(([costoUnitario, e]) => ({
            fecha_inicio: e.fecha_inicio,
            fecha_fin: e.fecha_fin,
            subtotales: { numero: extra.numero || 1, precio_unitario: costoUnitario, total: e.total },
        }))

        return {
            tarifa_id: rt.tarifa_id,
            tarifa: rt.tarifa,
            total: entradas.reduce((acc, e) => acc + (e.subtotales?.total ?? 0), 0),
            entradas,
        }
    })
}

const DetailsExtras = ({
    extras,
    tipoAlojamiento,
    reservas_tarifas = [],
}: {
    extras: Extra[]
    tipoAlojamiento: TiposAlojamientos
    reservas_tarifas?: ReservaTarifa[]
}) => {
    const { UTCStringToLocalDate } = useDate()
    const { formatCustomDate } = useFormatDate()

    return (
        <>
            {extras
                .filter((e) => e.tipo_extra === TiposExtras.Hospedaje || e.tipo_extra === TiposExtras.Persona || e.tipo_extra === TiposExtras.Hora)
                .map((extra, idx) => {
                    const esHospedaje = extra.tipo_extra === TiposExtras.Hospedaje
                    const esHora = extra.tipo_extra === TiposExtras.Hora
                    const esGrupoMotel = tipoAlojamiento === TiposAlojamientos.Motel
                    const esMotel = esHospedaje && esGrupoMotel

                    const rawTarifas = extra.extras_tarifas || []
                    const grupos = rawTarifas.length > 0
                        ? groupExtrasTarifasByTarifa(rawTarifas)
                        : buildGruposFromReservaTarifas(extra, reservas_tarifas)

                    const label = esHospedaje
                        ? esMotel ? "Hospedajes extra" : "Noches extra"
                        : esHora ? "Horas extra" : "Personas extra"
                    const icon = esHospedaje ? "MoonFill" : esHora ? "Clock" : "UserParentFill"
                    return (
                        <ResumenSection key={`extra-${idx}`}>
                            <Detail color='var(--tipografa)'>
                                <DetailIcon name={icon} />
                                <DetailContent>
                                    <DetailRow>
                                        <DetailText type='normal'>{label}</DetailText>
                                        <DetailText textAlign="right" type='normal'>
                                            {formatCurrencyToFixed(extra.monto_extra || 0)}
                                        </DetailText>
                                    </DetailRow>
                                    {esMotel
                                        ? <EntradaHospedajeMotel grupos={grupos} />
                                        : grupos.map((grupo, gIdx) => (
                                            <Fragment key={`extra-grupo-${gIdx}`}>
                                                <DetailRow>
                                                    {
                                                        grupo.tarifa?.grupo_tarifa?.modalidad === ModalidadGrupoTarifa.Base ?
                                                            <DetailText type='bold'>{capitalize(grupo.tarifa?.grupo_tarifa?.modalidad)}</DetailText> :
                                                            <DetailText type='bold'>{capitalize(grupo.tarifa?.grupo_tarifa?.modalidad || "")} - {grupo.tarifa?.grupo_tarifa?.nombre || ""}</DetailText>
                                                    }
                                                </DetailRow>
                                                {esHospedaje && (
                                                    <DetailRow>
                                                        <DetailText type='bold'>
                                                            {sum(grupo.entradas.map((e) => e.subtotales?.numero || 0))} noches
                                                        </DetailText>
                                                    </DetailRow>
                                                )}
                                                {(esHospedaje || esHora ? grupo.entradas : mergeEntradasByPrecio(grupo.entradas)).map((entry, eIdx) => {
                                                    const inicioDate = UTCStringToLocalDate(entry.fecha_inicio)
                                                    const finDate = UTCStringToLocalDate(entry.fecha_fin)

                                                    const diffDays = (finDate.getTime() - inicioDate.getTime()) / (1000 * 60 * 60 * 24)
                                                    const fechaLabel = diffDays >= 1
                                                        ? `${formatCustomDate(inicioDate, "MMM, DD")}-${formatCustomDate(finDate, "MMM, DD")}`
                                                        : formatCustomDate(inicioDate, "MMM, DD")
                                                    return (
                                                        <DetailRow key={`extra-entry-${eIdx}`}>
                                                            <DetailText style={{ maxWidth: "200px" }}>
                                                                {esHospedaje
                                                                    ? `${fechaLabel} | ${formatCurrencyToFixed(entry.subtotales?.precio_unitario || 0)}`
                                                                    : esHora
                                                                        ? `1 hora | ${formatCurrencyToFixed(entry.subtotales?.precio_unitario || 0)}`
                                                                        : `${extra.numero || 1} persona${(extra.numero || 1) > 1 ? 's' : ''} x ${esGrupoMotel ? 'estancia' : 'noche'} | ${formatCurrencyToFixed(entry.subtotales?.precio_unitario || 0)} x pers.`
                                                                }
                                                            </DetailText>
                                                            <DetailText type="bold" textAlign="right" style={{ maxWidth: "75px" }}>
                                                                {formatCurrencyToFixed(entry.subtotales?.total || 0)}
                                                            </DetailText>
                                                        </DetailRow>
                                                    )
                                                })}
                                            </Fragment>
                                        ))
                                    }
                                </DetailContent>
                            </Detail>
                        </ResumenSection>
                    )
                })
            }
        </>
    )
}

export default DetailsExtras
